import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Receipt, Scale, PieChart } from "lucide-react";
import axios from "axios";
import { Spinner } from "../components/common";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* PUBLIC read-only trip expense summary — reachable via share link and embeddable
   in an iframe on partner sites (?embed=1 hides the site chrome via Layout). */

const money = (v, ccy) => `${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy || ""}`.trim();

export default function SharedExpenses() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [data, setData] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/expenses/shared/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);

  if (data === undefined) return <Spinner full />;
  if (data === null) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="shared-expenses-dead">
      <p className="font-heading font-700 text-xl">This expense link is no longer active</p>
      <p className="text-sm text-muted-foreground mt-2">The trip owner may have turned sharing off.</p>
    </div>
  );

  const cats = Object.entries(data.by_category || {}).sort((a, b) => b[1] - a[1]);
  const catMax = Math.max(1, ...cats.map(([, v]) => v));
  const balances = Object.entries(data.balances || {});

  return (
    <div className={`max-w-3xl mx-auto px-4 sm:px-5 ${embed ? "py-4" : "py-10"}`} data-testid="shared-expenses">
      <div className="border border-zinc-300 bg-white">
        <div className="bg-emerald-950 text-white px-5 py-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 flex items-center gap-1.5">
            <Receipt className="w-3.5 h-3.5" /> Trip expense summary · {SITE.name || "TravelerPulse"}
          </div>
          <h1 className="font-heading font-900 text-2xl mt-1" data-testid="shared-expenses-title">{data.title || "Trip"}</h1>
          <div className="text-xs text-white/70 mt-0.5">
            {[data.start_date, data.end_date].filter(Boolean).join(" – ")}
            {data.stops?.length ? ` · ${data.stops.join(" → ")}` : ""}
            {data.shared_by ? ` · shared by ${data.shared_by}` : ""}
          </div>
        </div>

        <div className="p-5 grid sm:grid-cols-3 gap-3 text-center border-b border-zinc-200">
          <div><div className="text-[11px] font-mono uppercase text-muted-foreground">Total spent</div>
            <div className="font-heading font-900 text-2xl" data-testid="shared-expenses-total">{money(data.total, data.home_currency)}</div></div>
          <div><div className="text-[11px] font-mono uppercase text-muted-foreground">Expenses</div>
            <div className="font-heading font-900 text-2xl">{data.count}</div></div>
          <div><div className="text-[11px] font-mono uppercase text-muted-foreground">Budget</div>
            <div className="font-heading font-900 text-2xl">{data.budget_total ? money(data.budget_total, data.home_currency) : "—"}</div></div>
        </div>

        {cats.length > 0 && (
          <div className="p-5 border-b border-zinc-200" data-testid="shared-expenses-categories">
            <div className="text-sm font-800 flex items-center gap-1.5 mb-3"><PieChart className="w-4 h-4 text-emerald-700" /> Where the money went</div>
            <div className="space-y-1.5">
              {cats.map(([cat, v]) => (
                <div key={cat} className="flex items-center gap-2 text-sm">
                  <span className="w-28 capitalize text-zinc-700 truncate">{cat}</span>
                  <span className="flex-1 bg-zinc-100 h-4 relative"><span className="absolute inset-y-0 left-0 bg-emerald-500/70" style={{ width: `${(v / catMax) * 100}%` }} /></span>
                  <span className="font-mono text-xs w-24 text-right">{money(v, data.home_currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(balances.length > 0 || (data.transfers || []).length > 0) ? (
          <div className="p-5" data-testid="shared-expenses-settlement">
            <div className="text-sm font-800 flex items-center gap-1.5 mb-3"><Scale className="w-4 h-4 text-emerald-700" /> Settlement — who owes whom</div>
            {balances.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {balances.map(([name, v]) => (
                  <span key={name} className={`px-2.5 py-1 border text-xs font-700 ${v >= 0 ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-800"}`}>
                    {name}: {v >= 0 ? "+" : ""}{money(v, data.home_currency)}
                  </span>
                ))}
              </div>
            )}
            {(data.transfers || []).length === 0
              ? <p className="text-sm text-muted-foreground">All settled — nobody owes anything.</p>
              : (
                <ul className="space-y-1 text-sm">
                  {data.transfers.map((t, i) => (
                    <li key={i} className="flex items-center gap-2" data-testid={`shared-transfer-${i}`}>
                      <span className="font-700">{t.from}</span> <span className="text-muted-foreground">pays</span>
                      <span className="font-700">{t.to}</span>
                      <span className="ml-auto font-mono">{money(t.amount, data.home_currency)}</span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        ) : (
          <div className="p-5" data-testid="shared-expenses-settlement-empty">
            <div className="text-sm font-800 flex items-center gap-1.5 mb-1.5"><Scale className="w-4 h-4 text-emerald-700" /> Settlement</div>
            <p className="text-sm text-muted-foreground">Nothing to settle yet — no shared or split expenses on this trip so far.</p>
          </div>
        )}

        <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-200 text-[11px] text-muted-foreground flex flex-wrap items-center gap-1">
          Read-only summary — receipts, notes and documents stay private.
          <a href={SITE.url || "https://travelerpulse.pro"} target="_blank" rel="noreferrer" className="ml-auto font-700 text-emerald-700 hover:underline">
            Track your trip with {SITE.name || "TravelerPulse"} →
          </a>
        </div>
      </div>
    </div>
  );
}
